import React, { useState, useEffect } from 'react';
import {
  FileText, Upload, Download, Trash2, X, Paperclip
} from 'lucide-react';
import { documentAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const Documents = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [establishments, setEstablishments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    description: '',
    category: 'general'
  });

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lockedEstId) fetchEstablishments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEstablishment) {
      fetchDocuments();
    }
  }, [selectedEstablishment]);

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      const estabs = response.establishments || [];
      setEstablishments(estabs);
      if (estabs.length > 0) {
        setSelectedEstablishment(estabs[0].id.toString());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setEstablishments([]);
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!selectedEstablishment) return;

    try {
      setLoading(true);
      console.log('Fetching documents for organization:', selectedEstablishment);
      const response = await documentAPI.getEstablishmentDocuments(selectedEstablishment);
      console.log('Documents response:', response);
      console.log('Number of documents:', response.documents?.length || 0);
      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      console.error('Error details:', error.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    if (!selectedEstablishment) {
      alert('Please select an organization');
      return;
    }

    try {
      setUploading(true);
      const response = await documentAPI.uploadEstablishmentDocument(
        selectedEstablishment,
        selectedFile,
        uploadFormData.description,
        uploadFormData.category
      );
      console.log('Upload response:', response);
      notifySavedAndReload('Document uploaded successfully!');
      resetUploadForm();

      // Add a small delay before refreshing to account for S3 eventual consistency
      setTimeout(() => {
        fetchDocuments();
      }, 1000);
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFormData({
      title: '',
      description: '',
      category: 'general'
    });
    setSelectedFile(null);
    setShowUploadModal(false);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentAPI.deleteEstablishmentDocument(selectedEstablishment, doc.file_path);
      notifySavedAndReload('Document deleted successfully!');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Use the presigned download URL directly from doc
      if (doc.download_url) {
        const link = document.createElement('a');
        link.href = doc.download_url;
        link.download = doc.file_name || doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback to old method
        const response = await documentAPI.getDownloadUrl(doc.id);
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = doc.file_name || doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  return (
    <div className="attendance-container" style={{ padding: '1.5rem' }}>
      {/* Header with Upload Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem', margin: 0 }}>
            Documents & Resources
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            Access and manage organization documents
          </p>
        </div>
        {selectedEstablishment && (
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => fetchDocuments()}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
              title="Refresh document list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Upload size={16} />
              Upload
            </button>
          </div>
        )}
      </div>

      {/* Establishment Selector */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
          loading={loading}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--theme-surface)',
            borderRadius: '0.5rem',
            width: '90%',
            maxWidth: '500px',
            padding: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>Upload Document</h2>
              <button
                onClick={resetUploadForm}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* File Input */}
              <div>
                <label className="new-employee-field-label">
                  <Paperclip size={14} style={{ marginRight: '6px' }} />
                  Select File <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="new-employee-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                />
                {selectedFile && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="new-employee-field-label">
                  <FileText size={14} style={{ marginRight: '6px' }} />
                  Description
                </label>
                <textarea
                  value={uploadFormData.description}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
                  className="new-employee-input"
                  placeholder="e.g., Company registration certificate issued on 2024"
                  rows="3"
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Category */}
              <div>
                <label className="new-employee-field-label">
                  Category
                </label>
                <select
                  value={uploadFormData.category}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, category: e.target.value })}
                  className="new-employee-input"
                >
                  <option value="general">General</option>
                  <option value="legal">Legal</option>
                  <option value="compliance">Compliance</option>
                  <option value="contract">Contract</option>
                  <option value="certificate">Certificate</option>
                  <option value="policy">Policy</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Form Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              marginTop: '1.5rem'
            }}>
              <button
                onClick={resetUploadForm}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--theme-surfaceHover)',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: (uploading || !selectedFile) ? '#9ca3af' : '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: (uploading || !selectedFile) ? 'not-allowed' : 'pointer'
                }}
              >
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {selectedEstablishment && (
        <div style={{ background: 'var(--theme-surface)', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                No documents uploaded yet
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: 'var(--theme-surfaceHover)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    File Name
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Description
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Category
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Size
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Uploaded
                  </th>
                  <th style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', width: '100px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Paperclip size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                            {doc.file_name || doc.fileName || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            {doc.file_type || '-'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {doc.description || '-'}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.625rem',
                        background: 'var(--theme-surfaceHover)',
                        color: '#6b7280',
                        fontSize: '0.75rem',
                        borderRadius: '0.25rem',
                        textTransform: 'capitalize'
                      }}>
                        {doc.category || 'General'}
                      </span>
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {doc.file_size || doc.fileSize || '-'}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {doc.uploaded_date || doc.uploadedDate || '-'}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleDownload(doc)}
                          style={{
                            padding: '0.375rem',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            color: '#2563eb',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          style={{
                            padding: '0.375rem',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            color: '#dc2626',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Documents;
